import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, isToday, isBefore } from 'date-fns';
import { cn } from '@/lib/utils';
import {
  Calendar as CalendarIcon, Plus, ChevronLeft, ChevronRight, Clock, Shield,
  AlertTriangle, CheckCircle, XCircle, Users, Building2, Plane, FolderOpen,
  Target, RefreshCw, Play, Download, FileText, BarChart3, Settings,
  Repeat, Bell, Layers, List, Eye, Edit2, Trash2, TrendingUp, 
  AlertCircle, Flag, Gauge, Timer, Zap, ArrowRight
} from 'lucide-react';

export default function AuditProgrammePage() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const { isDark } = useTheme();
  
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('calendar');
  
  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Data state
  const [programmes, setProgrammes] = useState([]);
  const [activeProgramme, setActiveProgramme] = useState(null);
  const [scheduledAudits, setScheduledAudits] = useState([]);
  const [auditUniverse, setAuditUniverse] = useState({ entities: [], total_entities: 0 });
  const [recurringTemplates, setRecurringTemplates] = useState([]);
  const [readinessWidget, setReadinessWidget] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  
  // Dialog state
  const [showCreateProgramme, setShowCreateProgramme] = useState(false);
  const [showCreateAudit, setShowCreateAudit] = useState(false);
  const [showCreateEntity, setShowCreateEntity] = useState(false);
  const [showCreateRecurring, setShowCreateRecurring] = useState(false);
  const [showEventDetails, setShowEventDetails] = useState(false);
  
  // Form state
  const [programmeForm, setProgrammeForm] = useState({
    name: '',
    description: '',
    year: new Date().getFullYear(),
    start_date: new Date(new Date().getFullYear(), 0, 1),
    end_date: new Date(new Date().getFullYear(), 11, 31),
    coverage_target: 100
  });
  
  const [auditForm, setAuditForm] = useState({
    title: '',
    description: '',
    audit_type: '',
    auditable_entity_id: '',
    scope: 'organization',
    scheduled_date: null,
    duration_days: 1,
    priority: 'medium',
    is_recurring: false,
    frequency: 'one_time'
  });
  
  const [entityForm, setEntityForm] = useState({
    name: '',
    entity_type: 'department',
    description: '',
    risk_score: 50,
    required_audit_types: []
  });

  // Audit types
  const auditTypes = [
    { id: 'gaca_part5', name: 'GACA Part 5' },
    { id: 'easa_oro_gen', name: 'EASA ORO.GEN' },
    { id: 'faa_part5', name: 'FAA Part 5' },
    { id: 'iosa', name: 'IOSA' },
    { id: 'internal', name: 'Internal' }
  ];

  // Fetch data
  const fetchProgrammes = useCallback(async () => {
    try {
      const response = await api.get('/audit-programme/programmes');
      setProgrammes(response.data.programmes || []);
      // Set active programme to the current year's programme if exists
      const currentYear = new Date().getFullYear();
      const active = response.data.programmes?.find(p => p.year === currentYear && p.status === 'active');
      if (active) setActiveProgramme(active);
    } catch (error) {
      console.error('Failed to fetch programmes:', error);
    }
  }, [api]);

  const fetchCalendar = useCallback(async () => {
    try {
      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();
      const response = await api.get(`/audit-programme/calendar?month=${month}&year=${year}`);
      setCalendarEvents(response.data.events || []);
    } catch (error) {
      console.error('Failed to fetch calendar:', error);
    }
  }, [api, currentMonth]);

  const fetchScheduledAudits = useCallback(async () => {
    try {
      const response = await api.get('/audit-programme/scheduled');
      setScheduledAudits(response.data.audits || []);
    } catch (error) {
      console.error('Failed to fetch scheduled audits:', error);
    }
  }, [api]);

  const fetchAuditUniverse = useCallback(async () => {
    try {
      const response = await api.get('/audit-programme/universe');
      setAuditUniverse(response.data);
    } catch (error) {
      console.error('Failed to fetch audit universe:', error);
    }
  }, [api]);

  const fetchRecurringTemplates = useCallback(async () => {
    try {
      const response = await api.get('/audit-programme/recurring-templates');
      setRecurringTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch recurring templates:', error);
    }
  }, [api]);

  const fetchReadinessWidget = useCallback(async () => {
    try {
      const response = await api.get('/audit-programme/widget/readiness');
      setReadinessWidget(response.data);
    } catch (error) {
      console.error('Failed to fetch readiness widget:', error);
    }
  }, [api]);

  const fetchSuggestions = useCallback(async () => {
    try {
      const response = await api.get('/audit-programme/suggestions/from-gaps');
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchProgrammes();
    fetchAuditUniverse();
    fetchRecurringTemplates();
    fetchReadinessWidget();
    fetchSuggestions();
  }, [fetchProgrammes, fetchAuditUniverse, fetchRecurringTemplates, fetchReadinessWidget, fetchSuggestions]);

  useEffect(() => {
    fetchCalendar();
    fetchScheduledAudits();
  }, [fetchCalendar, fetchScheduledAudits, currentMonth]);

  // Create programme
  const handleCreateProgramme = async () => {
    if (!programmeForm.name) {
      toast.error('Please enter programme name');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/audit-programme/programmes', {
        ...programmeForm,
        start_date: programmeForm.start_date.toISOString(),
        end_date: programmeForm.end_date.toISOString()
      });
      toast.success('Audit programme created');
      setShowCreateProgramme(false);
      fetchProgrammes();
      setProgrammeForm({
        name: '',
        description: '',
        year: new Date().getFullYear(),
        start_date: new Date(new Date().getFullYear(), 0, 1),
        end_date: new Date(new Date().getFullYear(), 11, 31),
        coverage_target: 100
      });
    } catch (error) {
      toast.error('Failed to create programme');
    }
    setLoading(false);
  };

  // Create scheduled audit
  const handleCreateAudit = async () => {
    if (!auditForm.title || !auditForm.audit_type || !auditForm.scheduled_date) {
      toast.error('Please fill required fields');
      return;
    }
    
    if (!activeProgramme) {
      toast.error('Please create and activate a programme first');
      return;
    }
    
    setLoading(true);
    try {
      await api.post(`/audit-programme/scheduled?programme_id=${activeProgramme.id}`, {
        ...auditForm,
        scheduled_date: auditForm.scheduled_date.toISOString()
      });
      toast.success('Audit scheduled');
      setShowCreateAudit(false);
      fetchCalendar();
      fetchScheduledAudits();
      setAuditForm({
        title: '',
        description: '',
        audit_type: '',
        auditable_entity_id: '',
        scope: 'organization',
        scheduled_date: null,
        duration_days: 1,
        priority: 'medium',
        is_recurring: false,
        frequency: 'one_time'
      });
    } catch (error) {
      toast.error('Failed to schedule audit');
    }
    setLoading(false);
  };

  // Create auditable entity
  const handleCreateEntity = async () => {
    if (!entityForm.name) {
      toast.error('Please enter entity name');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/audit-programme/universe/entities', entityForm);
      toast.success('Entity added to audit universe');
      setShowCreateEntity(false);
      fetchAuditUniverse();
      setEntityForm({
        name: '',
        entity_type: 'department',
        description: '',
        risk_score: 50,
        required_audit_types: []
      });
    } catch (error) {
      toast.error('Failed to create entity');
    }
    setLoading(false);
  };

  // Auto-populate universe
  const handleAutoPopulate = async () => {
    setLoading(true);
    try {
      const response = await api.post('/audit-programme/universe/auto-populate');
      toast.success(`Added ${response.data.created_count} entities from existing data`);
      fetchAuditUniverse();
    } catch (error) {
      toast.error('Failed to auto-populate');
    }
    setLoading(false);
  };

  // Activate programme
  const handleActivateProgramme = async (programmeId) => {
    try {
      await api.post(`/audit-programme/programmes/${programmeId}/approve`);
      toast.success('Programme activated');
      fetchProgrammes();
    } catch (error) {
      toast.error('Failed to activate programme');
    }
  };

  // Complete audit
  const handleCompleteAudit = async (auditId) => {
    try {
      await api.post(`/audit-programme/scheduled/${auditId}/complete`);
      toast.success('Audit marked as completed');
      fetchCalendar();
      fetchScheduledAudits();
      fetchReadinessWidget();
    } catch (error) {
      toast.error('Failed to complete audit');
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    const colors = {
      critical: 'bg-red-500',
      high: 'bg-orange-500',
      medium: 'bg-amber-500',
      low: 'bg-emerald-500'
    };
    return colors[priority] || 'bg-blue-500';
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'scheduled':
        return <Badge className="bg-blue-600"><Clock className="w-3 h-3 mr-1" />Scheduled</Badge>;
      case 'in_progress':
        return <Badge className="bg-purple-600"><Play className="w-3 h-3 mr-1" />In Progress</Badge>;
      case 'completed':
        return <Badge className="bg-emerald-600"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'overdue':
        return <Badge className="bg-red-600"><AlertTriangle className="w-3 h-3 mr-1" />Overdue</Badge>;
      case 'cancelled':
        return <Badge className="bg-slate-600"><XCircle className="w-3 h-3 mr-1" />Cancelled</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  // Get entity type icon
  const getEntityIcon = (type) => {
    switch (type) {
      case 'organization': return Building2;
      case 'department': return Users;
      case 'base': return FolderOpen;
      case 'aircraft': return Plane;
      case 'process': return Settings;
      default: return Layers;
    }
  };

  // Calendar day component
  const CalendarDay = ({ date, events }) => {
    const dayEvents = events.filter(e => {
      const eventDate = new Date(e.start);
      return isSameDay(eventDate, date);
    });
    
    const isCurrentMonth = isSameMonth(date, currentMonth);
    const isSelected = selectedDate && isSameDay(date, selectedDate);
    const isPast = isBefore(date, new Date()) && !isToday(date);
    
    return (
      <div 
        className={cn(
          "min-h-[100px] p-1 border-r border-b cursor-pointer transition-colors",
          isDark ? "border-slate-700" : "border-gray-200",
          !isCurrentMonth && (isDark ? "bg-slate-900/50" : "bg-gray-50"),
          isSelected && (isDark ? "bg-emerald-900/30" : "bg-emerald-50"),
          isToday(date) && "ring-2 ring-emerald-500 ring-inset",
          "hover:bg-emerald-900/20"
        )}
        onClick={() => setSelectedDate(date)}
      >
        <div className={cn(
          "text-sm font-medium mb-1",
          !isCurrentMonth && (isDark ? "text-slate-600" : "text-gray-400"),
          isToday(date) && "text-emerald-400"
        )}>
          {format(date, 'd')}
        </div>
        <div className="space-y-1">
          {dayEvents.slice(0, 3).map((event, idx) => (
            <div
              key={idx}
              className={cn(
                "text-xs px-1 py-0.5 rounded truncate cursor-pointer",
                event.status === 'overdue' ? 'bg-red-600 text-white' :
                event.status === 'completed' ? 'bg-emerald-600 text-white' :
                `${getPriorityColor(event.priority)} text-white`
              )}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedEvent(event);
                setShowEventDetails(true);
              }}
            >
              {event.title}
            </div>
          ))}
          {dayEvents.length > 3 && (
            <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
              +{dayEvents.length - 3} more
            </div>
          )}
        </div>
      </div>
    );
  };

  // Generate calendar days
  const calendarDays = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  });

  // Pad start of month to align with weekday
  const startPadding = startOfMonth(currentMonth).getDay();
  const paddedDays = [...Array(startPadding).fill(null), ...calendarDays];

  return (
    <div className={cn("space-y-6 p-6", isDark ? "bg-slate-950" : "bg-gray-50")} data-testid="audit-programme-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn("text-2xl font-bold flex items-center gap-3", isDark ? "text-white" : "text-gray-900")}>
            <CalendarIcon className="w-8 h-8 text-emerald-500" />
            Audit Programme Scheduler
          </h1>
          <p className={cn("mt-1", isDark ? "text-slate-400" : "text-gray-500")}>
            Risk-based audit planning with automated scheduling and reminders
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowCreateProgramme(true)}
            variant="outline"
            className={cn(isDark ? "border-slate-600" : "border-gray-300")}
          >
            <FolderOpen className="w-4 h-4 mr-2" />
            New Programme
          </Button>
          <Button 
            onClick={() => {
              if (!activeProgramme) {
                toast.error('Please create and activate a programme first');
                return;
              }
              setShowCreateAudit(true);
            }}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Schedule Audit
          </Button>
        </div>
      </div>

      {/* Readiness Widget */}
      {readinessWidget && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className={cn(
            "border-2",
            readinessWidget.days_until_next !== null && readinessWidget.days_until_next <= 7 
              ? "border-amber-500/50" 
              : isDark ? "border-slate-700" : "border-gray-200",
            isDark ? "bg-slate-800/50" : "bg-white"
          )}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Next Audit</p>
                  {readinessWidget.next_audit ? (
                    <>
                      <p className={cn("text-lg font-bold", isDark ? "text-white" : "text-gray-900")}>
                        {readinessWidget.days_until_next} days
                      </p>
                      <p className={cn("text-xs truncate max-w-[150px]", isDark ? "text-slate-400" : "text-gray-500")}>
                        {readinessWidget.next_audit.title}
                      </p>
                    </>
                  ) : (
                    <p className={cn("text-lg", isDark ? "text-slate-500" : "text-gray-400")}>None scheduled</p>
                  )}
                </div>
                <div className="p-3 bg-emerald-900/30 rounded-lg">
                  <CalendarIcon className="w-6 h-6 text-emerald-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Readiness Score</p>
                  <p className={cn(
                    "text-3xl font-bold",
                    readinessWidget.readiness_score >= 80 ? "text-emerald-400" :
                    readinessWidget.readiness_score >= 60 ? "text-amber-400" : "text-red-400"
                  )}>
                    {readinessWidget.readiness_score ? `${Math.round(readinessWidget.readiness_score)}%` : 'N/A'}
                  </p>
                  <div className="flex items-center gap-1 mt-1">
                    {readinessWidget.readiness_trend === 'improving' && (
                      <TrendingUp className="w-3 h-3 text-emerald-400" />
                    )}
                    <span className={cn("text-xs capitalize", isDark ? "text-slate-400" : "text-gray-500")}>
                      {readinessWidget.readiness_trend}
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-blue-900/30 rounded-lg">
                  <Gauge className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={cn(
            readinessWidget.overdue_count > 0 ? "border-red-500/50" : "",
            isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200"
          )}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Overdue</p>
                  <p className={cn(
                    "text-3xl font-bold",
                    readinessWidget.overdue_count > 0 ? "text-red-400" : "text-emerald-400"
                  )}>
                    {readinessWidget.overdue_count}
                  </p>
                </div>
                <div className={cn("p-3 rounded-lg", readinessWidget.overdue_count > 0 ? "bg-red-900/30" : "bg-emerald-900/30")}>
                  {readinessWidget.overdue_count > 0 ? (
                    <AlertTriangle className="w-6 h-6 text-red-400" />
                  ) : (
                    <CheckCircle className="w-6 h-6 text-emerald-400" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>This Month</p>
                  <p className={cn("text-3xl font-bold", isDark ? "text-white" : "text-gray-900")}>
                    {readinessWidget.this_month_count}
                  </p>
                  <p className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                    {readinessWidget.critical_gaps} critical gaps
                  </p>
                </div>
                <div className="p-3 bg-purple-900/30 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className={cn("border", isDark ? "bg-slate-800 border-slate-700" : "bg-gray-100 border-gray-200")}>
          <TabsTrigger value="calendar" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            <CalendarIcon className="w-4 h-4 mr-2" />
            Calendar
          </TabsTrigger>
          <TabsTrigger value="list" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            <List className="w-4 h-4 mr-2" />
            Audit List
          </TabsTrigger>
          <TabsTrigger value="universe" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            <Layers className="w-4 h-4 mr-2" />
            Audit Universe
          </TabsTrigger>
          <TabsTrigger value="programmes" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            <FolderOpen className="w-4 h-4 mr-2" />
            Programmes
          </TabsTrigger>
          <TabsTrigger value="recurring" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">
            <Repeat className="w-4 h-4 mr-2" />
            Recurring
          </TabsTrigger>
        </TabsList>

        {/* Calendar Tab */}
        <TabsContent value="calendar" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Calendar */}
            <Card className={cn("lg:col-span-3", isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                    {format(currentMonth, 'MMMM yyyy')}
                  </CardTitle>
                  <div className="flex gap-1">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setCurrentMonth(new Date())}
                    >
                      Today
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Weekday headers */}
                <div className={cn("grid grid-cols-7 border-l border-t", isDark ? "border-slate-700" : "border-gray-200")}>
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div 
                      key={day} 
                      className={cn(
                        "p-2 text-center text-sm font-medium border-r border-b",
                        isDark ? "bg-slate-900 border-slate-700 text-slate-300" : "bg-gray-50 border-gray-200 text-gray-700"
                      )}
                    >
                      {day}
                    </div>
                  ))}
                </div>
                {/* Calendar grid */}
                <div className={cn("grid grid-cols-7 border-l", isDark ? "border-slate-700" : "border-gray-200")}>
                  {paddedDays.map((date, idx) => (
                    date ? (
                      <CalendarDay key={idx} date={date} events={calendarEvents} />
                    ) : (
                      <div key={idx} className={cn(
                        "min-h-[100px] border-r border-b",
                        isDark ? "bg-slate-900/50 border-slate-700" : "bg-gray-50 border-gray-200"
                      )} />
                    )
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Sidebar */}
            <div className="space-y-4">
              {/* Selected Date Audits */}
              {selectedDate && (
                <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
                  <CardHeader className="pb-2">
                    <CardTitle className={cn("text-base", isDark ? "text-white" : "text-gray-900")}>
                      {format(selectedDate, 'MMMM d, yyyy')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {calendarEvents.filter(e => isSameDay(new Date(e.start), selectedDate)).map((event, idx) => (
                        <div 
                          key={idx}
                          className={cn(
                            "p-2 rounded cursor-pointer transition-colors",
                            isDark ? "bg-slate-900/50 hover:bg-slate-900" : "bg-gray-50 hover:bg-gray-100"
                          )}
                          onClick={() => {
                            setSelectedEvent(event);
                            setShowEventDetails(true);
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <div className={cn("w-2 h-2 rounded-full", getPriorityColor(event.priority))} />
                            <span className={cn("text-sm font-medium", isDark ? "text-white" : "text-gray-900")}>
                              {event.title}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">{event.audit_type}</Badge>
                            {getStatusBadge(event.status)}
                          </div>
                        </div>
                      ))}
                      {calendarEvents.filter(e => isSameDay(new Date(e.start), selectedDate)).length === 0 && (
                        <p className={cn("text-sm text-center py-4", isDark ? "text-slate-500" : "text-gray-400")}>
                          No audits scheduled
                        </p>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full mt-2"
                        onClick={() => {
                          setAuditForm(prev => ({ ...prev, scheduled_date: selectedDate }));
                          setShowCreateAudit(true);
                        }}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Audit
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Suggestions */}
              {suggestions.length > 0 && (
                <Card className={cn("border-amber-500/50", isDark ? "bg-slate-800/50" : "bg-white")}>
                  <CardHeader className="pb-2">
                    <CardTitle className={cn("text-base flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                      <Zap className="w-4 h-4 text-amber-400" />
                      Suggested Audits
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {suggestions.slice(0, 3).map((suggestion, idx) => (
                        <div 
                          key={idx}
                          className={cn("p-2 rounded", isDark ? "bg-amber-900/20" : "bg-amber-50")}
                        >
                          <div className="flex items-center justify-between">
                            <Badge className={getPriorityColor(suggestion.suggested_priority)}>
                              {suggestion.audit_type?.toUpperCase()}
                            </Badge>
                            <span className="text-xs text-amber-400">{suggestion.suggested_priority}</span>
                          </div>
                          <p className={cn("text-xs mt-1", isDark ? "text-slate-400" : "text-gray-600")}>
                            Score: {suggestion.last_simulation_score?.toFixed(0)}% • {suggestion.gaps_summary?.total} gaps
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Audit List Tab */}
        <TabsContent value="list" className="space-y-4">
          <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
            <CardHeader>
              <CardTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                <List className="w-5 h-5 text-emerald-400" />
                Scheduled Audits
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-3">
                  {scheduledAudits.map(audit => (
                    <div 
                      key={audit.id}
                      className={cn(
                        "p-4 rounded-lg border",
                        audit.status === 'overdue' && "border-red-500/50",
                        isDark ? "bg-slate-900/50 border-slate-700" : "bg-gray-50 border-gray-200"
                      )}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>
                              {audit.title}
                            </h3>
                            <Badge className={getPriorityColor(audit.priority)}>{audit.priority}</Badge>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-xs">
                              {auditTypes.find(t => t.id === audit.audit_type)?.name || audit.audit_type}
                            </Badge>
                            {getStatusBadge(audit.status)}
                            {audit.is_recurring && (
                              <Badge variant="outline" className="text-xs border-purple-500 text-purple-400">
                                <Repeat className="w-3 h-3 mr-1" />
                                {audit.frequency}
                              </Badge>
                            )}
                          </div>
                          <p className={cn("text-sm mt-2", isDark ? "text-slate-400" : "text-gray-500")}>
                            <CalendarIcon className="w-3 h-3 inline mr-1" />
                            {format(new Date(audit.scheduled_date), 'PPP')}
                            {audit.lead_auditor_name && (
                              <span className="ml-3">
                                <Users className="w-3 h-3 inline mr-1" />
                                {audit.lead_auditor_name}
                              </span>
                            )}
                          </p>
                          {audit.last_simulation_score !== null && (
                            <p className={cn("text-xs mt-1", isDark ? "text-slate-500" : "text-gray-400")}>
                              Last simulation: {audit.last_simulation_score?.toFixed(0)}% readiness
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {audit.status === 'scheduled' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleCompleteAudit(audit.id)}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {scheduledAudits.length === 0 && (
                    <div className={cn("text-center py-12", isDark ? "text-slate-500" : "text-gray-400")}>
                      <CalendarIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-medium">No audits scheduled</p>
                      <p className="text-sm mt-1">Create a programme and schedule your first audit</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Universe Tab */}
        <TabsContent value="universe" className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex gap-4">
              <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                <span className="font-bold text-lg mr-1">{auditUniverse.total_entities}</span> entities
              </div>
              <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                <span className="font-bold text-lg mr-1 text-amber-400">{auditUniverse.high_risk_entities}</span> high risk
              </div>
              <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                <span className="font-bold text-lg mr-1 text-emerald-400">{auditUniverse.coverage_percentage?.toFixed(0)}%</span> coverage
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleAutoPopulate} disabled={loading}>
                <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
                Auto-Populate
              </Button>
              <Button onClick={() => setShowCreateEntity(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Entity
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {auditUniverse.entities?.map(entity => {
              const EntityIcon = getEntityIcon(entity.entity_type);
              return (
                <Card 
                  key={entity.id}
                  className={cn(
                    entity.risk_score >= 70 && "border-red-500/50",
                    isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200"
                  )}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className={cn(
                          "p-2 rounded",
                          entity.risk_score >= 70 ? "bg-red-900/30" : "bg-emerald-900/30"
                        )}>
                          <EntityIcon className={cn(
                            "w-5 h-5",
                            entity.risk_score >= 70 ? "text-red-400" : "text-emerald-400"
                          )} />
                        </div>
                        <div>
                          <h3 className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>
                            {entity.name}
                          </h3>
                          <p className={cn("text-xs capitalize", isDark ? "text-slate-400" : "text-gray-500")}>
                            {entity.entity_type}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={cn(
                          "text-lg font-bold",
                          entity.risk_score >= 70 ? "text-red-400" : 
                          entity.risk_score >= 50 ? "text-amber-400" : "text-emerald-400"
                        )}>
                          {entity.risk_score}
                        </div>
                        <p className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-400")}>Risk Score</p>
                      </div>
                    </div>
                    
                    {entity.days_since_audit !== null && (
                      <p className={cn(
                        "text-xs mb-2",
                        entity.days_since_audit > 365 ? "text-red-400" : isDark ? "text-slate-400" : "text-gray-500"
                      )}>
                        <Clock className="w-3 h-3 inline mr-1" />
                        Last audited: {entity.days_since_audit} days ago
                      </p>
                    )}
                    
                    <div className="flex flex-wrap gap-1">
                      {entity.required_audit_types?.map((type, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {type}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            
            {auditUniverse.entities?.length === 0 && (
              <Card className={cn("col-span-full", isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
                <CardContent className={cn("py-12 text-center", isDark ? "text-slate-500" : "text-gray-400")}>
                  <Layers className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">No entities in audit universe</p>
                  <p className="text-sm mt-1">Add entities or auto-populate from existing data</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Programmes Tab */}
        <TabsContent value="programmes" className="space-y-4">
          <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
            <CardHeader>
              <CardTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                <FolderOpen className="w-5 h-5 text-emerald-400" />
                Audit Programmes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {programmes.map(programme => (
                  <div 
                    key={programme.id}
                    className={cn(
                      "p-4 rounded-lg border",
                      programme.status === 'active' && "border-emerald-500/50",
                      isDark ? "bg-slate-900/50 border-slate-700" : "bg-gray-50 border-gray-200"
                    )}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>
                            {programme.name}
                          </h3>
                          <Badge className={
                            programme.status === 'active' ? 'bg-emerald-600' :
                            programme.status === 'draft' ? 'bg-slate-600' :
                            programme.status === 'completed' ? 'bg-blue-600' : 'bg-slate-600'
                          }>
                            {programme.status}
                          </Badge>
                        </div>
                        <p className={cn("text-sm mt-1", isDark ? "text-slate-400" : "text-gray-500")}>
                          {programme.year} • {format(new Date(programme.start_date), 'MMM d')} - {format(new Date(programme.end_date), 'MMM d, yyyy')}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-4">
                          <div>
                            <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>
                              {programme.completed || 0}/{programme.total_scheduled || 0}
                            </p>
                            <p className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-400")}>Completed</p>
                          </div>
                          {programme.status === 'draft' && (
                            <Button 
                              size="sm"
                              onClick={() => handleActivateProgramme(programme.id)}
                              className="bg-emerald-600 hover:bg-emerald-700"
                            >
                              Activate
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {programme.total_scheduled > 0 && (
                      <div className="mt-4">
                        <div className="flex justify-between text-xs mb-1">
                          <span className={isDark ? "text-slate-400" : "text-gray-500"}>Progress</span>
                          <span className={isDark ? "text-slate-400" : "text-gray-500"}>
                            {((programme.completed / programme.total_scheduled) * 100).toFixed(0)}%
                          </span>
                        </div>
                        <Progress value={(programme.completed / programme.total_scheduled) * 100} className="h-2" />
                      </div>
                    )}
                  </div>
                ))}
                
                {programmes.length === 0 && (
                  <div className={cn("text-center py-12", isDark ? "text-slate-500" : "text-gray-400")}>
                    <FolderOpen className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">No programmes yet</p>
                    <p className="text-sm mt-1">Create your first audit programme to get started</p>
                    <Button 
                      className="mt-4 bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => setShowCreateProgramme(true)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Programme
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Recurring Tab */}
        <TabsContent value="recurring" className="space-y-4">
          <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                  <Repeat className="w-5 h-5 text-purple-400" />
                  Recurring Audit Templates
                </CardTitle>
                <Button 
                  onClick={() => setShowCreateRecurring(true)}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Template
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recurringTemplates.map(template => (
                  <div 
                    key={template.id}
                    className={cn(
                      "p-4 rounded-lg border",
                      isDark ? "bg-slate-900/50 border-slate-700" : "bg-gray-50 border-gray-200"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>
                          {template.name}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">{template.audit_type}</Badge>
                          <Badge variant="outline" className="text-xs border-purple-500 text-purple-400">
                            {template.frequency}
                          </Badge>
                        </div>
                      </div>
                      <Button variant="outline" size="sm">
                        <Play className="w-4 h-4 mr-1" />
                        Generate
                      </Button>
                    </div>
                  </div>
                ))}
                
                {recurringTemplates.length === 0 && (
                  <div className={cn("text-center py-12", isDark ? "text-slate-500" : "text-gray-400")}>
                    <Repeat className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">No recurring templates</p>
                    <p className="text-sm mt-1">Create templates for automated audit scheduling</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Programme Dialog */}
      <Dialog open={showCreateProgramme} onOpenChange={setShowCreateProgramme}>
        <DialogContent className={cn("max-w-md", isDark ? "bg-slate-900 border-slate-700" : "bg-white")}>
          <DialogHeader>
            <DialogTitle className={cn(isDark ? "text-white" : "text-gray-900")}>
              Create Audit Programme
            </DialogTitle>
            <DialogDescription className={isDark ? "text-slate-400" : "text-gray-500"}>
              Set up an annual audit programme
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Programme Name *</Label>
              <Input
                value={programmeForm.name}
                onChange={(e) => setProgrammeForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Annual Audit Programme 2025"
                className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Year</Label>
              <Select
                value={programmeForm.year.toString()}
                onValueChange={(v) => setProgrammeForm(prev => ({ ...prev, year: parseInt(v) }))}
              >
                <SelectTrigger className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027].map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Coverage Target (%)</Label>
              <Input
                type="number"
                value={programmeForm.coverage_target}
                onChange={(e) => setProgrammeForm(prev => ({ ...prev, coverage_target: parseFloat(e.target.value) }))}
                className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateProgramme(false)}>Cancel</Button>
            <Button onClick={handleCreateProgramme} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
              Create Programme
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Audit Dialog */}
      <Dialog open={showCreateAudit} onOpenChange={setShowCreateAudit}>
        <DialogContent className={cn("max-w-lg", isDark ? "bg-slate-900 border-slate-700" : "bg-white")}>
          <DialogHeader>
            <DialogTitle className={cn(isDark ? "text-white" : "text-gray-900")}>
              Schedule Audit
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={auditForm.title}
                onChange={(e) => setAuditForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., GACA SMS Audit Q1"
                className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Audit Type *</Label>
                <Select
                  value={auditForm.audit_type}
                  onValueChange={(v) => setAuditForm(prev => ({ ...prev, audit_type: v }))}
                >
                  <SelectTrigger className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {auditTypes.map(type => (
                      <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={auditForm.priority}
                  onValueChange={(v) => setAuditForm(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Scheduled Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className={cn("w-full justify-start", isDark ? "bg-slate-800 border-slate-700" : "")}
                  >
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {auditForm.scheduled_date 
                      ? format(auditForm.scheduled_date, 'PPP')
                      : 'Select date'
                    }
                  </Button>
                </PopoverTrigger>
                <PopoverContent className={cn("w-auto p-0", isDark ? "bg-slate-900 border-slate-700" : "")}>
                  <Calendar
                    mode="single"
                    selected={auditForm.scheduled_date}
                    onSelect={(date) => setAuditForm(prev => ({ ...prev, scheduled_date: date }))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            <div className="space-y-2">
              <Label>Entity (Optional)</Label>
              <Select
                value={auditForm.auditable_entity_id}
                onValueChange={(v) => setAuditForm(prev => ({ ...prev, auditable_entity_id: v === 'organization-wide' ? '' : v }))}
              >
                <SelectTrigger className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
                  <SelectValue placeholder="Select entity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="organization-wide">Organization-wide</SelectItem>
                  {auditUniverse.entities?.map(entity => (
                    <SelectItem key={entity.id} value={entity.id}>{entity.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateAudit(false)}>Cancel</Button>
            <Button onClick={handleCreateAudit} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
              Schedule Audit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Entity Dialog */}
      <Dialog open={showCreateEntity} onOpenChange={setShowCreateEntity}>
        <DialogContent className={cn("max-w-md", isDark ? "bg-slate-900 border-slate-700" : "bg-white")}>
          <DialogHeader>
            <DialogTitle className={cn(isDark ? "text-white" : "text-gray-900")}>
              Add Auditable Entity
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Entity Name *</Label>
              <Input
                value={entityForm.name}
                onChange={(e) => setEntityForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Flight Operations Department"
                className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Entity Type</Label>
              <Select
                value={entityForm.entity_type}
                onValueChange={(v) => setEntityForm(prev => ({ ...prev, entity_type: v }))}
              >
                <SelectTrigger className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="organization">Organization</SelectItem>
                  <SelectItem value="department">Department</SelectItem>
                  <SelectItem value="base">Base/Station</SelectItem>
                  <SelectItem value="aircraft">Aircraft</SelectItem>
                  <SelectItem value="process">Process</SelectItem>
                  <SelectItem value="supplier">Supplier</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Risk Score (0-100)</Label>
              <Input
                type="number"
                value={entityForm.risk_score}
                onChange={(e) => setEntityForm(prev => ({ ...prev, risk_score: parseFloat(e.target.value) }))}
                min={0}
                max={100}
                className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateEntity(false)}>Cancel</Button>
            <Button onClick={handleCreateEntity} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
              Add Entity
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event Details Dialog */}
      <Dialog open={showEventDetails} onOpenChange={setShowEventDetails}>
        <DialogContent className={cn("max-w-md", isDark ? "bg-slate-900 border-slate-700" : "bg-white")}>
          <DialogHeader>
            <DialogTitle className={cn(isDark ? "text-white" : "text-gray-900")}>
              Audit Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4">
              <div>
                <h3 className={cn("text-lg font-medium", isDark ? "text-white" : "text-gray-900")}>
                  {selectedEvent.title}
                </h3>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline">{selectedEvent.audit_type}</Badge>
                  {getStatusBadge(selectedEvent.status)}
                  <Badge className={getPriorityColor(selectedEvent.priority)}>{selectedEvent.priority}</Badge>
                </div>
              </div>
              
              <div className={cn("space-y-2 text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                <p>
                  <CalendarIcon className="w-4 h-4 inline mr-2" />
                  {format(new Date(selectedEvent.start), 'PPP')}
                </p>
                {selectedEvent.entity_name && (
                  <p>
                    <Building2 className="w-4 h-4 inline mr-2" />
                    {selectedEvent.entity_name}
                  </p>
                )}
                {selectedEvent.lead_auditor && (
                  <p>
                    <Users className="w-4 h-4 inline mr-2" />
                    {selectedEvent.lead_auditor}
                  </p>
                )}
                {selectedEvent.readiness_score !== null && (
                  <p>
                    <Gauge className="w-4 h-4 inline mr-2" />
                    Readiness: {selectedEvent.readiness_score?.toFixed(0)}%
                  </p>
                )}
              </div>
              
              {selectedEvent.status === 'scheduled' && (
                <Button 
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => {
                    handleCompleteAudit(selectedEvent.id);
                    setShowEventDetails(false);
                  }}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark as Completed
                </Button>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEventDetails(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
